import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { KEEP, REVIEWS_WIKI, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import {
  Alert,
  AlertProps,
  Box,
  Button,
  FlashbarProps,
  FormField,
  Modal,
  SpaceBetween,
  Textarea,
  Wizard,
} from '@amzn/awsui-components-react-v3';
import { scrollUp } from 'src/components/utils/navigation';
import { ReviewAgreements } from 'src/components/reviews/reviewAgreements';
import { validateContextForDataAgreement } from 'src/api/d3a';
import ReviewAssignment from 'src/components/reviews/reviewAssignment';
import {
  createNotificationHelpers,
  hasNonD3AFailures,
  submitReviewsByAgreement,
  updateAgreement,
} from 'src/components/reviews/utils';
import { ReviewAgreementsResponseList, ReviewReviewerDecisionList } from 'src/components/reviews/types';
import { Link } from 'src/components/common/Link';
import { FORM_CONTEXTS_FOR_REVIEWS } from 'src/components/datausageagreement/utils';

export interface ReviewWizardProps {
  setContentType: React.Dispatch<React.SetStateAction<string>>;
  match: any;
  username: string;
  userInfo: any;
  setNotifications: React.Dispatch<React.SetStateAction<FlashbarProps.MessageDefinition[]>>;
}

export const ReviewWizard = (props: ReviewWizardProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [resourceItems, setResourceItems] = useState([]);

  const [decidedResources, setDecidedResources] = useState<ReviewReviewerDecisionList>([]);
  const [reviewersByAgreement, setReviewersByAgreement] = useState<ReviewAgreementsResponseList>([]);
  const { showError, showSuccess, dismissNotifications } = createNotificationHelpers(props.setNotifications);
  const [comment, setComment] = useState<string>();
  const [areYouSureModalVisible, setAreYouSureModalVisible] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalAlertType, setModalAlertType] = useState<AlertProps.Type>('info');

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
  }, []);

  const handleSubmit = async () => {
    setButtonLoading(true);
    try {
      await submitReviewsByAgreement(reviewersByAgreement, 'CONSUMER', comment);
      setActiveStepIndex(0);
      showSuccess(`Successfully submitted reviews`);
    } catch (e) {
      console.log(e);
      setButtonLoading(false);
      showError(`Failed to submit reviews: ${e.message}`);
    }
    scrollUp();
    setButtonLoading(false);
  };

  const nonDeferredResources = useMemo(
    () => decidedResources.filter((decidedResource) => decidedResource.action !== 'Defer'),
    [decidedResources, resourceItems],
  );

  const dismissModal = () => {
    setModalShown(true);
    setAreYouSureModalVisible(false);
  };

  const showModal = (title: string, message: string, type: AlertProps.Type = 'info') => {
    setModalTitle(title);
    setModalMessage(message);
    setModalAlertType(type);
    setAreYouSureModalVisible(true);
  };

  const steps = useMemo(
    () => [
      {
        title: 'Review resources assigned to you',
        info: (
          <Link variant='info' href={REVIEWS_WIKI} external={true}>
            Info
          </Link>
        ),
        description: 'Assign review actions to all the resources before proceeding.',
        content: (
          <>
            <Modal
              visible={areYouSureModalVisible}
              header={modalTitle}
              onDismiss={dismissModal}
              footer={
                <Box float='right'>
                  <SpaceBetween direction='horizontal' size='xs'>
                    <Button variant='primary' onClick={dismissModal}>
                      Ok
                    </Button>
                  </SpaceBetween>
                </Box>
              }
            >
              <Alert type={modalAlertType}>{modalMessage}</Alert>
            </Modal>
            <ReviewAssignment
              {...props}
              decidedResources={decidedResources}
              setDecidedResources={setDecidedResources}
              resourceItems={resourceItems}
              setResourceItems={setResourceItems}
            />
          </>
        ),
      },
      {
        title: 'Review questionnaire',
        description:
          'Verify all information below and update as necessary. Forms with identical answers will be consolidated in the next review cycle.',
        content: (
          <>
            <ReviewAgreements
              setContentType={props.setContentType}
              reviewersByAgreement={reviewersByAgreement}
              setReviewersByAgreement={setReviewersByAgreement}
              match={props.match}
              reviews={nonDeferredResources}
              includeAgreementLinks={true}
              filter={(review) => review['action'] == KEEP}
            />
          </>
        ),
      },
      {
        title: 'Summary',
        description: 'The following changes will be made when this form is submitted.',
        content: (
          <SpaceBetween size='m'>
            <ReviewAssignment
              {...props}
              decidedResources={decidedResources}
              setDecidedResources={setDecidedResources}
              readonly={true}
              resourceItems={resourceItems}
              setResourceItems={setResourceItems}
            />
            <FormField description='Add a comment or observations for the reviewer.' label='Comment'>
              <Textarea
                placeholder='Type your comment here...'
                value={comment}
                onChange={(e) => setComment(e.detail.value)}
              />
            </FormField>
          </SpaceBetween>
        ),
      },
    ],
    [
      props.setContentType,
      props.match,
      decidedResources,
      reviewersByAgreement,
      resourceItems,
      setDecidedResources,
      comment,
      areYouSureModalVisible,
    ],
  );

  const handleNavigate = async ({ detail }) => {
    if (steps[activeStepIndex].title == 'Review resources assigned to you') {
      if (decidedResources?.length == 0) {
        showModal('No review actions', 'You must assign at least one review action before proceeding.', 'error');
        return;
      }
      if (!modalShown && resourceItems?.length != 0) {
        showModal(
          'Incomplete review actions',
          'Some resources haven\'t been assigned review actions. These resources will be set to "Defer" by default if you continue. You can return to complete your review at any time before the deadline.',
          'warning',
        );
        return;
      }
    } else if (steps[activeStepIndex].title == 'Review questionnaire') {
      let valid = true;

      if (detail.requestedStepIndex > activeStepIndex) {
        await Promise.all(
          reviewersByAgreement.map(async ({ agreement, reviewers }, idx) => {
            let needsValidate = reviewers.filter((review) => review['action'] == KEEP).length > 0;
            if (needsValidate) {
              if (
                agreement.d3AValidationOutput?.isValid === undefined ||
                agreement.d3AValidationOutput?.isValid === false
              ) {
                try {
                  setButtonLoading(true);
                  const validateContextForDataAgreementOutput = await validateContextForDataAgreement({
                    id: agreement.agreementId,
                    context: agreement.agreementData ? JSON.parse(agreement.agreementData) : {},
                    validationContexts: FORM_CONTEXTS_FOR_REVIEWS,
                  });
                  updateAgreement(setReviewersByAgreement, idx, {
                    d3AValidationOutput: validateContextForDataAgreementOutput,
                  });
                  if (!validateContextForDataAgreementOutput.isValid && !agreement.overrideReason) {
                    valid = false;
                  }
                } catch (e) {
                  console.log('Error when validating form inputs: ', e);
                  valid = false;
                } finally {
                  setButtonLoading(false);
                }
              }
            }
          }),
        );
        // Check after fresh validation so we always inspect the latest output
        if (hasNonD3AFailures(reviewersByAgreement)) {
          valid = false;
        }
      }

      if (!valid) {
        scrollUp();
        showError(
          'Form validation failed. Review the errors below and either correct the issues or provide an override reason to continue.',
        );
        return;
      }
    }
    dismissNotifications();
    setActiveStepIndex(detail.requestedStepIndex);
  };

  return (
    <>
      <Wizard
        i18nStrings={{
          stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
          navigationAriaLabel: 'Steps',
          cancelButton: 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          optional: 'optional',
        }}
        onSubmit={handleSubmit}
        submitButtonText='Confirm and submit'
        onNavigate={handleNavigate}
        activeStepIndex={activeStepIndex}
        allowSkipTo
        steps={steps}
        isLoadingNextStep={buttonLoading}
      />
    </>
  );
};
